import { MeshData, bounds, vertexCount } from '../geo/mesh';
import { Vec3 } from '../math/vec3';

export const FLOATS_PER_VERTEX = 12; // pos3 nrm3 col3 emi3
export const INSTANCE_STRIDE = 20; // mat4 + rgb + emissive mix

/** Mesh uploaded to the GPU. Optionally carries a per-instance matrix buffer. */
export class GpuMesh {
  readonly vao: WebGLVertexArrayObject;
  readonly vbo: WebGLBuffer;
  readonly ibo: WebGLBuffer;
  readonly indexCount: number;
  readonly vertexCount: number;
  readonly center = new Vec3();
  readonly radius: number;
  instanceBuffer: WebGLBuffer | null = null;
  instanceCount = 0;
  /** Bounding sphere for the whole instanced set (world space). */
  readonly instCenter = new Vec3();
  instRadius = 0;

  constructor(readonly gl: WebGL2RenderingContext, data: MeshData, readonly name = 'mesh') {
    const vc = vertexCount(data);
    const buf = new Float32Array(vc * FLOATS_PER_VERTEX);
    for (let i = 0; i < vc; i++) {
      const o = i * FLOATS_PER_VERTEX;
      buf[o] = data.positions[i * 3]; buf[o + 1] = data.positions[i * 3 + 1]; buf[o + 2] = data.positions[i * 3 + 2];
      buf[o + 3] = data.normals[i * 3]; buf[o + 4] = data.normals[i * 3 + 1]; buf[o + 5] = data.normals[i * 3 + 2];
      buf[o + 6] = data.colors[i * 3]; buf[o + 7] = data.colors[i * 3 + 1]; buf[o + 8] = data.colors[i * 3 + 2];
      buf[o + 9] = data.emissive[i * 3]; buf[o + 10] = data.emissive[i * 3 + 1]; buf[o + 11] = data.emissive[i * 3 + 2];
    }
    this.vertexCount = vc;
    this.indexCount = data.indices.length;
    const b = bounds(data);
    if (b.isEmpty()) { this.radius = 0; } else {
      b.center(this.center);
      this.radius = b.size().length() * 0.5;
    }

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    this.vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, buf, gl.STATIC_DRAW);
    const stride = FLOATS_PER_VERTEX * 4;
    for (let a = 0; a < 4; a++) {
      gl.enableVertexAttribArray(a);
      gl.vertexAttribPointer(a, 3, gl.FLOAT, false, stride, a * 12);
    }
    this.ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(data.indices), gl.STATIC_DRAW);
    gl.bindVertexArray(null);
  }

  /** Upload per-instance data: 20 floats each = world matrix (16) + rgb tint (3) + emissive mix (1). */
  setInstances(mats: Float32Array, dynamic = false): void {
    const gl = this.gl;
    gl.bindVertexArray(this.vao);
    if (!this.instanceBuffer) {
      this.instanceBuffer = gl.createBuffer()!;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, mats, dynamic ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW);
    for (let i = 0; i < 5; i++) {
      const loc = 4 + i;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, INSTANCE_STRIDE * 4, i * 16);
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.bindVertexArray(null);
    this.instanceCount = Math.floor(mats.length / INSTANCE_STRIDE);
    // bounding sphere of instance set: union of transformed mesh spheres
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < this.instanceCount; i++) {
      const o = i * INSTANCE_STRIDE;
      const cx = mats[o] * this.center.x + mats[o + 4] * this.center.y + mats[o + 8] * this.center.z + mats[o + 12];
      const cy = mats[o + 1] * this.center.x + mats[o + 5] * this.center.y + mats[o + 9] * this.center.z + mats[o + 13];
      const cz = mats[o + 2] * this.center.x + mats[o + 6] * this.center.y + mats[o + 10] * this.center.z + mats[o + 14];
      const s = Math.sqrt(Math.max(
        mats[o] * mats[o] + mats[o + 1] * mats[o + 1] + mats[o + 2] * mats[o + 2],
        mats[o + 4] * mats[o + 4] + mats[o + 5] * mats[o + 5] + mats[o + 6] * mats[o + 6],
        mats[o + 8] * mats[o + 8] + mats[o + 9] * mats[o + 9] + mats[o + 10] * mats[o + 10]));
      const r = this.radius * s;
      minX = Math.min(minX, cx - r); maxX = Math.max(maxX, cx + r);
      minY = Math.min(minY, cy - r); maxY = Math.max(maxY, cy + r);
      minZ = Math.min(minZ, cz - r); maxZ = Math.max(maxZ, cz + r);
    }
    if (this.instanceCount > 0) {
      this.instCenter.set((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
      this.instRadius = Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) * 0.5;
    }
  }

  /** Helper: write one instance record. */
  static writeInstance(buf: Float32Array, i: number, m: Float32Array, r = 1, g = 1, b = 1, e = 0): void {
    const o = i * INSTANCE_STRIDE;
    buf.set(m, o);
    buf[o + 16] = r; buf[o + 17] = g; buf[o + 18] = b; buf[o + 19] = e;
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteVertexArray(this.vao); gl.deleteBuffer(this.vbo); gl.deleteBuffer(this.ibo);
    if (this.instanceBuffer) gl.deleteBuffer(this.instanceBuffer);
  }
}
